import React, { useEffect, useMemo, useRef } from 'react';
import {
  formatRating,
  getRatingColorLevel,
  getMrpMonthRatingBreakdown,
  getMrpYearRatingBreakdown,
  getCohortRatingBreakdown,
  getExpectedHours,
  formatPlanHours,
  formatMrpYearMonthRowHint,
  MRP_MONTH_LABELS,
  MRP_MONTH_LABELS_SHORT,
} from '../utils/periodSummaryRating';
import { getMrpRatingConfig } from '../utils/mrpRatingSettings';
import { SHIFTS_AND_ONLINE_ENABLED } from '../constants/featureFlags';
import { MRP_METRICS, calcActivityScore } from '../utils/mrpStatisticsMetrics';

function ratingCfg() {
  return getMrpRatingConfig();
}

function isUsingMetricRates() {
  const rates = ratingCfg().metricRates || ratingCfg().settings?.metric_rates || {};
  return Object.values(rates).some(
    (rate) => (Number(rate?.every) || 0) > 0 && (Number(rate?.points) || 0) > 0
  );
}

function getMetricRate(key) {
  const rates = ratingCfg().metricRates || ratingCfg().settings?.metric_rates || {};
  const row = rates[key] || {};
  return {
    every: Math.max(0, Number(row.every) || 0),
    points: Math.max(0, Number(row.points) || 0),
  };
}

function BreakdownGrid({ children, headers = ['Показатель', 'Кол-во', 'Баллы'] }) {
  return (
    <div className="sp-rbd-table-wrap">
      <table className="sp-rbd-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={`${h}-${i}`}
                className={
                  i === 0
                    ? 'sp-rbd-th sp-rbd-th--label'
                    : i === headers.length - 1
                      ? 'sp-rbd-th sp-rbd-th--value'
                      : 'sp-rbd-th sp-rbd-th--meta'
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Row({ label, value, meta, hint, variant = 'default', level, cols = 3 }) {
  const levelClass = level ? ` sp-rbd-tr--${level}` : '';
  const showMeta = cols >= 3;
  return (
    <tr
      className={`sp-rbd-tr sp-rbd-tr--${variant}${levelClass}`}
      title={hint || undefined}
    >
      <td className="sp-rbd-td sp-rbd-td--label">{label}</td>
      {showMeta ? (
        <td className="sp-rbd-td sp-rbd-td--meta">{meta != null && meta !== '' ? meta : '—'}</td>
      ) : null}
      <td className="sp-rbd-td sp-rbd-td--value">{value}</td>
    </tr>
  );
}

function MetricRatesBreakdown({ mod, periodType, year, month, breakdown: b }) {
  const totalMetrics = mod.totalMetrics || {};
  const hours = SHIFTS_AND_ONLINE_ENABLED ? (mod.totalMinutes || 0) / 60 : 0;
  const expectedHours = SHIFTS_AND_ONLINE_ENABLED
    ? getExpectedHours(periodType, year, month, mod.role)
    : 0;
  const totalLevel = getRatingColorLevel(b.total);
  const hoursRounded = Math.round(hours * 10) / 10;
  const totalCount = MRP_METRICS.reduce((sum, metric) => {
    const rate = getMetricRate(metric.key);
    if (!(rate.every > 0 && rate.points > 0)) return sum;
    return sum + (Number(totalMetrics[metric.key]) || 0);
  }, 0);

  return (
    <BreakdownGrid>
      {SHIFTS_AND_ONLINE_ENABLED && (
        <Row
          label="Онлайн"
          meta={expectedHours > 0 ? `${hoursRounded}/${formatPlanHours(expectedHours)} ч` : `${hoursRounded} ч`}
          value={b.hoursPoints > 0 ? `+${formatRating(b.hoursPoints)}` : '—'}
        />
      )}
      {MRP_METRICS.map((metric) => {
        const rate = getMetricRate(metric.key);
        if (!(rate.every > 0 && rate.points > 0)) return null;
        const count = Number(totalMetrics[metric.key]) || 0;
        const earned = Number(b.metricPointsByKey?.[metric.key]) || 0;
        return (
          <Row
            key={metric.key}
            label={metric.label}
            meta={String(count)}
            value={earned > 0 ? `+${formatRating(earned)}` : '—'}
            hint={`${metric.label}: каждые ${rate.every} = +${formatRating(rate.points)}`}
          />
        );
      })}
      {SHIFTS_AND_ONLINE_ENABLED && b.overtimeBonus > 0 && (
        <Row label="Переработка" value={`+${formatRating(b.overtimeBonus)}`} />
      )}
      {b.afkPenalty > 0 && (
        <Row label="AFK" meta={String(b.afkKicks)} value={`−${formatRating(b.afkPenalty)}`} />
      )}
      {b.reprimandPenalty > 0 && (
        <Row label="Выговоры" meta={String(b.reprimands)} value={`−${formatRating(b.reprimandPenalty)}`} />
      )}
      <Row
        label="Итог"
        meta={String(totalCount)}
        value={formatRating(b.total)}
        variant="total"
        level={totalLevel}
      />
    </BreakdownGrid>
  );
}

function LegacyThresholdBreakdown({ mod, periodType, year, month, breakdown: b }) {
  const totalMetrics = mod.totalMetrics || {};
  const hours = SHIFTS_AND_ONLINE_ENABLED
    ? (mod.totalMinutes || 0) / 60
    : calcActivityScore(totalMetrics);
  const punishments =
    (Number(totalMetrics.punishments_discord) || 0) +
    (Number(totalMetrics.punishments_as) || 0);
  const chatResponses =
    (Number(totalMetrics.chat_responses_discord) || 0) +
    (Number(totalMetrics.chat_responses_as) || 0);
  const expectedHours = SHIFTS_AND_ONLINE_ENABLED
    ? getExpectedHours(periodType, year, month, mod.role)
    : 0;

  const hoursRounded = Math.round(hours * 10) / 10;
  const totalLevel = getRatingColorLevel(b.total);
  const totalCount =
    punishments + ((b.chatResponseBonus > 0 || chatResponses > 0) ? chatResponses : 0);

  return (
    <BreakdownGrid>
      <Row
        label={SHIFTS_AND_ONLINE_ENABLED ? 'Онлайн' : 'Активность'}
        meta={
          SHIFTS_AND_ONLINE_ENABLED
            ? expectedHours > 0
              ? `${hoursRounded}/${formatPlanHours(expectedHours)} ч`
              : `${hoursRounded} ч`
            : String(Math.round(hours * 10) / 10)
        }
        value={`+${formatRating(b.hoursPoints)}`}
      />
      <Row label="Наказания" meta={String(punishments)} value={`+${formatRating(b.punishPoints)}`} />
      {(b.chatResponseBonus > 0 || chatResponses > 0) && (
        <Row
          label="Чат"
          meta={String(chatResponses)}
          value={b.chatResponseBonus > 0 ? `+${formatRating(b.chatResponseBonus)}` : '—'}
        />
      )}
      {SHIFTS_AND_ONLINE_ENABLED && b.overtimeBonus > 0 && (
        <Row label="Переработка" value={`+${formatRating(b.overtimeBonus)}`} />
      )}
      {b.afkPenalty > 0 && (
        <Row label="AFK" meta={String(b.afkKicks)} value={`−${formatRating(b.afkPenalty)}`} />
      )}
      {b.reprimandPenalty > 0 && (
        <Row label="Выговоры" meta={String(b.reprimands)} value={`−${formatRating(b.reprimandPenalty)}`} />
      )}
      <Row
        label="Итог"
        meta={String(totalCount)}
        value={formatRating(b.total)}
        variant="total"
        level={totalLevel}
      />
    </BreakdownGrid>
  );
}

function MrpThresholdBreakdown({ mod, periodType, year, month }) {
  const totalMetrics = mod.totalMetrics || {};
  const hours = SHIFTS_AND_ONLINE_ENABLED
    ? (mod.totalMinutes || 0) / 60
    : calcActivityScore(totalMetrics);
  const punishments =
    (Number(totalMetrics.punishments_discord) || 0) +
    (Number(totalMetrics.punishments_as) || 0);
  const afkKicks = mod.totalAfkKicks || 0;
  const reprimands = mod.totalReprimands || 0;
  const chatResponses =
    (Number(totalMetrics.chat_responses_discord) || 0) +
    (Number(totalMetrics.chat_responses_as) || 0);
  const expectedHours = SHIFTS_AND_ONLINE_ENABLED
    ? getExpectedHours(periodType, year, month, mod.role)
    : 0;

  const b =
    periodType === 'year'
      ? getMrpYearRatingBreakdown(
          hours,
          punishments,
          afkKicks,
          reprimands,
          {
            role: mod.role,
            created_at: mod.created_at,
            restored_at: mod.restored_at,
            deleted_at: mod.deleted_at,
            afkKicksByMonth: mod.afkKicksByMonth,
            reprimandsByMonth: mod.reprimandsByMonth,
            minutesByMonth: mod.minutesByMonth,
            punishmentsByMonth: mod.punishmentsByMonth,
            chatResponsesByMonth: mod.chatResponsesByMonth,
            metricsByMonth: mod.metricsByMonth,
          },
          expectedHours,
          chatResponses,
          year
        )
      : getMrpMonthRatingBreakdown(hours, punishments, afkKicks, reprimands, expectedHours, chatResponses, totalMetrics);

  const totalLevel = getRatingColorLevel(b.total);
  const useRates = isUsingMetricRates();

  if (b.fromMonthlyAverage) {
    return (
      <BreakdownGrid headers={['Месяц', 'Рейтинг']}>
        {(b.monthlyRatings || []).map((m) => (
          <Row
            key={m.month}
            cols={2}
            label={MRP_MONTH_LABELS[m.month - 1] || MRP_MONTH_LABELS_SHORT[m.month - 1]}
            value={formatRating(m.rating)}
            hint={formatMrpYearMonthRowHint(m)}
          />
        ))}
        <Row
          cols={2}
          label="Итог"
          value={formatRating(b.total)}
          variant="total"
          level={totalLevel}
        />
      </BreakdownGrid>
    );
  }

  if (useRates) {
    return (
      <MetricRatesBreakdown
        mod={mod}
        periodType={periodType}
        year={year}
        month={month}
        breakdown={b}
      />
    );
  }

  return (
    <LegacyThresholdBreakdown
      mod={mod}
      periodType={periodType}
      year={year}
      month={month}
      breakdown={b}
    />
  );
}

function CohortBreakdown({ mod, statType, cohort }) {
  const isMedia = statType === 'media';
  const value = isMedia ? parseFloat(mod.totalScreenHours) || 0 : mod.totalPunishments || 0;
  const cohortValues = cohort.map((m) =>
    isMedia ? parseFloat(m.totalScreenHours) || 0 : m.totalPunishments || 0
  );
  const b = getCohortRatingBreakdown(
    value,
    cohortValues,
    isMedia ? 'часов на стримах' : 'наказаний'
  );
  const totalLevel = getRatingColorLevel(b.total);

  return (
    <BreakdownGrid headers={['Показатель', 'Значение']}>
      <Row cols={2} label={isMedia ? 'Стримы' : 'Наказания'} value={`${value}`} />
      {b.maxV > b.minV && <Row cols={2} label="В сводке" value={`${b.minV}–${b.maxV}`} />}
      <Row
        cols={2}
        label="Итог"
        value={formatRating(b.total)}
        variant="total"
        level={totalLevel}
      />
    </BreakdownGrid>
  );
}

function resolveHeaderTotal(mod, statType, periodType, year, month) {
  if (statType !== 'mrp' || (periodType !== 'month' && periodType !== 'year')) {
    return Number(mod.rating) || 0;
  }
  const totalMetrics = mod.totalMetrics || {};
  const hours = SHIFTS_AND_ONLINE_ENABLED
    ? (mod.totalMinutes || 0) / 60
    : calcActivityScore(totalMetrics);
  const punishments =
    (Number(totalMetrics.punishments_discord) || 0) +
    (Number(totalMetrics.punishments_as) || 0);
  const afkKicks = mod.totalAfkKicks || 0;
  const reprimands = mod.totalReprimands || 0;
  const chatResponses =
    (Number(totalMetrics.chat_responses_discord) || 0) +
    (Number(totalMetrics.chat_responses_as) || 0);
  const expectedHours = SHIFTS_AND_ONLINE_ENABLED
    ? getExpectedHours(periodType, year, month, mod.role)
    : 0;

  if (periodType === 'year') {
    return getMrpYearRatingBreakdown(
      hours,
      punishments,
      afkKicks,
      reprimands,
      {
        role: mod.role,
        created_at: mod.created_at,
        restored_at: mod.restored_at,
        deleted_at: mod.deleted_at,
        afkKicksByMonth: mod.afkKicksByMonth,
        reprimandsByMonth: mod.reprimandsByMonth,
        minutesByMonth: mod.minutesByMonth,
        punishmentsByMonth: mod.punishmentsByMonth,
        chatResponsesByMonth: mod.chatResponsesByMonth,
        metricsByMonth: mod.metricsByMonth,
      },
      expectedHours,
      chatResponses,
      year
    ).total;
  }

  return getMrpMonthRatingBreakdown(
    hours,
    punishments,
    afkKicks,
    reprimands,
    expectedHours,
    chatResponses,
    totalMetrics
  ).total;
}

const PeriodSummaryRatingBreakdown = ({
  mod,
  statType,
  periodType,
  year,
  month,
  cohort,
  onClose,
}) => {
  const panelRef = useRef(null);

  const periodTag =
    periodType === 'year' ? 'Год' : periodType === 'month' ? 'Месяц' : String(periodType || '');

  const headerTotal = useMemo(
    () => resolveHeaderTotal(mod, statType, periodType, year, month),
    [mod, statType, periodType, year, month]
  );
  const headerLevel = getRatingColorLevel(headerTotal);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const onPointer = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [onClose]);

  const body =
    statType === 'mrp' && (periodType === 'month' || periodType === 'year') ? (
      <MrpThresholdBreakdown mod={mod} periodType={periodType} year={year} month={month} />
    ) : (
      <CohortBreakdown mod={mod} statType={statType} cohort={cohort} />
    );

  return (
    <div className="sp-rbd-overlay" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className="sp-rbd-panel sp-rbd-panel--doc"
        role="dialog"
        aria-labelledby="sp-rbd-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sp-rbd-header sp-rbd-header--doc">
          <div className="sp-rbd-header-main">
            <h3 id="sp-rbd-title">{mod.nickname}</h3>
            <p className="sp-rbd-sub">{periodTag}</p>
          </div>
          <div className={`sp-rbd-header-score sp-rbd-score--${headerLevel}`}>
            <span className="sp-rbd-header-score-val">{formatRating(headerTotal)}</span>
          </div>
          <button type="button" className="sp-rbd-close" onClick={onClose} aria-label="Закрыть">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="sp-rbd-body sp-rbd-body--doc">{body}</div>
      </div>
    </div>
  );
};

export default PeriodSummaryRatingBreakdown;
